import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  InventoryConfig,
  InventoryMovementRow,
  InventoryRow,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useScopedClientId } from "@/hooks/useScopedClientId";

export function useInventory(enabled = true) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["inventory", clientId],
    queryFn: () => api.get<{ items: InventoryRow[] }>("/crm/inventory"),
    enabled: enabled && !!clientId,
  });
}

export function useInventoryConfig(enabled = true) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["inventory", clientId, "config"],
    queryFn: () => api.get<InventoryConfig>("/crm/inventory/config"),
    enabled: enabled && !!clientId,
  });
}

export function useInventoryMovements(productId: string | null, page: number) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["inventory", clientId, productId, "movements", page],
    queryFn: () =>
      api.get<{ items: InventoryMovementRow[]; total: number }>(
        `/crm/inventory/${productId}/movements?page=${page}&pageSize=20`,
      ),
    enabled: !!clientId && !!productId,
  });
}

export function useAdjustStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { productId: string; onHand: number; reason?: string }) =>
      api.patch(`/crm/inventory/${vars.productId}`, {
        onHand: vars.onHand,
        ...(vars.reason ? { reason: vars.reason } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      // The catalog too: the product rows render the stock cell, and a stale
      // read would show the number the admin just changed.
      void queryClient.invalidateQueries({ queryKey: ["catalog"] });
      toast.success("Existencias actualizadas.");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useSetInventoryEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch<InventoryConfig>("/crm/inventory/config", { enabled }),
    onSuccess: (config) => {
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      toast.success(
        config.enabled
          ? "El agente ya consulta existencias reales."
          : "El agente dejó de hablar de existencias.",
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
